import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { CheckCircle2, AlertCircle, X } from "lucide-react";
import { cn } from "@/lib/cn";

type ToastKind = "success" | "error";

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  push: (message: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback((message: string, kind: ToastKind = "success") => {
    const id = nextId++;
    setItems((prev) => [...prev, { id, kind, message }]);
    setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, 4200);
  }, []);

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div className="pointer-events-none fixed bottom-6 right-6 z-50 flex max-w-sm flex-col gap-2">
        {items.map((t) => (
          <Toast key={t.id} item={t} onDismiss={() => setItems((p) => p.filter((x) => x.id !== t.id))} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  // Fade-in via mount transition.
  const [show, setShow] = useState(false);
  useEffect(() => { setShow(true); }, []);

  const Icon = item.kind === "success" ? CheckCircle2 : AlertCircle;
  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto flex items-start gap-2.5 rounded border bg-surface px-3.5 py-3 text-sm transition-all duration-200",
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1",
        item.kind === "success" ? "border-line text-text-strong" : "border-danger/30 text-danger",
      )}
    >
      <Icon
        size={16}
        strokeWidth={1.75}
        className={cn("mt-0.5 shrink-0", item.kind === "success" ? "text-success" : "text-danger")}
      />
      <p className="flex-1">{item.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-text-soft hover:text-text-strong"
      >
        <X size={14} strokeWidth={1.75} />
      </button>
    </div>
  );
}
